import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Gift, Copy, CheckCircle2, Share2, Users2, Coins, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

export default function Referrals() {
    const { locale } = useI18n();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [copied, setCopied] = useState(false);
    const bl = (en, fr) => (locale === "fr" ? fr : en);

    useEffect(() => {
        api.get("/referrals/my-code").then((r) => setData(r.data)).catch(() => {});
    }, []);

    async function copy(text) {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            toast.success(bl("Copied", "Copié", locale));
            setTimeout(() => setCopied(false), 2500);
        } catch (_e) { toast.error("Copy failed"); }
    }

    function shareLinkedIn() {
        const text = bl(
            "Je viens de découvrir BOXEB2B — la plateforme B2B pay-as-you-go pour les PME. 50 crédits offerts avec mon code :",
            "Je viens de découvrir BOXEB2B — la plateforme B2B pay-as-you-go pour les PME. 50 crédits offerts avec mon code :",
            locale
        );
        const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(data.share_url)}&summary=${encodeURIComponent(text)}`;
        window.open(url, "_blank", "width=600,height=500");
    }

    function shareWhatsApp() {
        const text = bl(
            `Je viens de découvrir BOXEB2B pour la prospection B2B. Utilise mon code pour 100 crédits offerts : ${data.share_url}`,
            `Je viens de découvrir BOXEB2B pour la prospection B2B. Utilise mon code pour 100 crédits offerts : ${data.share_url}`,
            locale
        );
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }

    if (!data) return <div className="p-10"><Loader2 className="animate-spin text-zinc-500" /></div>;

    return (
        <div className="px-6 lg:px-10 py-6 max-w-5xl mx-auto">
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-brand-cyan flex items-center gap-2 mb-1">
                    <Gift size={13} /> {bl("REFERRAL PROGRAM", "PROGRAMME DE PARRAINAGE")}
                </div>
                <h1 className="font-display font-black text-3xl tracking-tighter">
                    {bl("Invite a friend, both get", "Parraine un ami, vous recevez tous deux")}{" "}
                    <span className="text-brand-cyan">100 {bl("credits", "crédits")}</span>
                </h1>
                <p className="mt-2 text-sm text-zinc-400 max-w-2xl leading-relaxed">
                    {bl("They sign up with your code — you get 100 credits, they get 100 credits. No cap. No expiry.",
                        "Ils s'inscrivent avec ton code — tu reçois 100 crédits, ils reçoivent 100 crédits. Pas de limite. Pas d'expiration.", locale)}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <StatCard icon={Users2} label={bl("Referrals", "Parrainages")} value={data.referrals_count} testKey="ref-count" />
                <StatCard icon={Coins} label={bl("Credits earned", "Crédits gagnés")} value={data.credits_earned} tone="cyan" testKey="ref-earned" />
                <StatCard icon={Gift} label={bl("Per referral", "Par parrainage")} value={`+${data.bonus_per_referral}`} tone="violet" testKey="ref-bonus" />
            </div>

            {/* Code display */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl p-6 bg-gradient-to-br from-brand-indigo/20 to-brand-cyan/10 border border-brand-indigo/40 mb-6"
                data-testid="referral-code-card"
            >
                <div className="text-xs uppercase tracking-widest font-bold text-zinc-400 mb-3">{bl("Your referral code", "Ton code de parrainage")}</div>
                <div className="flex items-center gap-3 flex-wrap">
                    <div
                        className="font-display font-black text-4xl tracking-tighter text-white font-mono px-5 py-2 rounded-xl bg-brand-void border border-brand-indigo/30"
                        data-testid="referral-code"
                    >
                        {data.code}
                    </div>
                    <button
                        type="button"
                        onClick={() => copy(data.code)}
                        data-testid="referral-copy-code"
                        className="inline-flex items-center gap-2 text-sm text-white px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10"
                    >
                        {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                        {copied ? bl("Copied", "Copié") : bl("Copy code", "Copier")}
                    </button>
                </div>

                <div className="mt-5 flex items-center gap-2 flex-wrap">
                    <div className="flex-1 min-w-0 rounded-lg px-3 py-2 bg-brand-void border border-white/10 text-sm text-zinc-300 font-mono truncate" data-testid="referral-share-url">
                        {data.share_url}
                    </div>
                    <button
                        type="button"
                        onClick={() => copy(data.share_url)}
                        data-testid="referral-copy-url"
                        className="inline-flex items-center gap-1.5 text-sm text-white px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10"
                    >
                        <Copy size={13} />{bl("URL", "URL")}
                    </button>
                </div>
            </motion.div>

            <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3">{bl("Share in 1 click", "Partager en 1 clic")}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                <button
                    type="button"
                    onClick={shareLinkedIn}
                    data-testid="referral-share-linkedin"
                    className="rounded-xl p-4 bg-brand-surface border border-white/8 hover:border-brand-cyan/40 text-left transition"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#0A66C2] flex items-center justify-center text-white font-bold">in</div>
                        <div>
                            <div className="font-semibold text-white">{bl("Share on LinkedIn", "Partager sur LinkedIn")}</div>
                            <div className="text-xs text-zinc-500">{bl("Best for B2B network", "Idéal pour ton réseau B2B")}</div>
                        </div>
                    </div>
                </button>
                <button
                    type="button"
                    onClick={shareWhatsApp}
                    data-testid="referral-share-whatsapp"
                    className="rounded-xl p-4 bg-brand-surface border border-white/8 hover:border-brand-cyan/40 text-left transition"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#25D366] flex items-center justify-center">
                            <Share2 size={16} className="text-white" />
                        </div>
                        <div>
                            <div className="font-semibold text-white">{bl("Share on WhatsApp", "Partager sur WhatsApp")}</div>
                            <div className="text-xs text-zinc-500">{bl("For direct 1-1 outreach", "Pour du direct 1-1")}</div>
                        </div>
                    </div>
                </button>
            </div>

            <div className="rounded-2xl p-5 bg-brand-surface border border-white/8">
                <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-2">{bl("How it works", "Comment ça marche")}</div>
                <ol className="text-sm text-zinc-300 space-y-1.5 list-decimal pl-5 marker:text-brand-cyan">
                    <li>{bl("Share your code or URL with any B2B / SDR / founder friend.", "Partage ton code ou URL avec un ami B2B / SDR / founder.", locale)}</li>
                    <li>{bl("They register at BOXEB2B and enter your code.", "Il s'inscrit sur BOXEB2B et entre ton code.", locale)}</li>
                    <li>{bl("You BOTH get 100 credits credited immediately — no waiting, no cap.", "Vous recevez TOUS DEUX 100 crédits crédités immédiatement — sans attente, sans limite.", locale)}</li>
                </ol>
                <div className="mt-3 text-[11px] text-zinc-600 font-mono">
                    {bl("Signed-in as", "Connecté en tant que")} {user?.email}
                </div>
            </div>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, tone = "indigo", testKey }) {
    return (
        <div className="rounded-xl p-4 bg-brand-surface border border-white/8" data-testid={`stat-${testKey}`}>
            <div className="flex items-start justify-between">
                <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold">{label}</div>
                <Icon size={13} className={`text-brand-${tone}`} />
            </div>
            <div className="mt-2 font-display font-black text-2xl tracking-tighter">{value}</div>
        </div>
    );
}
